import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Wand2, Loader2, Sparkles, AlertCircle, Coins, Mic, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { videoStudioService } from '../../services/videoStudioService';
import { GeneratedVideo } from '../../types/videoStudio';
import AIPromptHelper from './AIPromptHelper';

interface Props {
  video: GeneratedVideo;
  onClose: () => void;
  onRegenerated: (newVideo: GeneratedVideo) => void;
}

/**
 * HeyGen doesn't expose an "edit existing video" API endpoint — every change
 * triggers a brand-new render. This modal is upfront about that:
 *   1. Pre-fills the original script + avatar/voice/aspect/background metadata
 *   2. Shows cost estimate before commit
 *   3. Submits a new /generate request linked back to the original via
 *      parameters.regenerated_from = original.id (so the user can see history)
 *   4. Streams progress and emits the new video on completion
 */
const HeygenEditModal: React.FC<Props> = ({ video, onClose, onRegenerated }) => {
  const { t } = useTranslation();
  const original = video.parameters || {};

  const [script, setScript] = useState(video.prompt || '');
  const [caption, setCaption] = useState(!!original.caption);
  const [estimate, setEstimate] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');

  const wordsCount = script.trim().split(/\s+/).filter(Boolean).length;
  const estSeconds = Math.max(5, Math.ceil(wordsCount / 2.5));

  // Live cost estimation as the user types
  useEffect(() => {
    let active = true;
    const h = setTimeout(() => {
      videoStudioService.estimate({
        provider: 'heygen',
        parameters: {
          duration: estSeconds,
          avatar_tier: original.avatar_tier || 'standard'
        }
      }).then(r => {
        if (active && r.success && r.data) setEstimate(r.data.cost);
      }).catch(() => {});
    }, 250);
    return () => { active = false; clearTimeout(h); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [script]);

  const dirty = script.trim() !== (video.prompt || '').trim() || caption !== !!original.caption;

  const handleRegenerate = async () => {
    if (!script.trim()) {
      toast.error(t('videoStudio.heygen.errScript') as string);
      return;
    }
    if (!original.avatar_id || !original.voice_id) {
      toast.error(t('videoStudio.heygenEdit.missingOriginalParams', 'Esse vídeo não guardou avatar/voz originais — gere um novo do zero') as string);
      return;
    }
    setGenerating(true);
    setProgress(0);
    setProgressLabel(t('videoStudio.heygen.progressUploading') as string);
    try {
      const res = await videoStudioService.generate(
        {
          provider: 'heygen',
          prompt: script,
          aspect_ratio: (video.aspect_ratio as any) || '16:9',
          title: video.title ? `${video.title} (v2)` : undefined,
          parameters: {
            avatar_id: original.avatar_id,
            voice_id: original.voice_id,
            background: original.background || '#0F172A',
            caption,
            avatar_tier: original.avatar_tier || 'standard',
            regenerated_from: video.id
          }
        },
        (pct) => {
          setProgress(pct);
          if (pct < 20) setProgressLabel(t('videoStudio.heygen.progressVoice') as string);
          else if (pct < 60) setProgressLabel(t('videoStudio.heygen.progressRecording') as string);
          else if (pct < 90) setProgressLabel(t('videoStudio.heygen.progressLipSync') as string);
          else setProgressLabel(t('videoStudio.heygen.progressSaving') as string);
        }
      );
      if (res.success && res.data) {
        toast.success(t('videoStudio.heygenEdit.regenerated', 'Vídeo regerado com sucesso') as string);
        onRegenerated(res.data);
        onClose();
      }
    } catch (e: any) {
      const code = e.response?.data?.code;
      if (code === 'INSUFFICIENT_VIDEO_CREDITS' || e.response?.status === 402) {
        toast.error(t('videoStudio.heygen.insufficientCredits') as string);
      } else {
        toast.error(e.response?.data?.message || e.message || (t('videoStudio.heygen.errGeneration') as string));
      }
    } finally {
      setGenerating(false);
      setProgress(0);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-gray-950/85 backdrop-blur-sm"
      onClick={generating ? undefined : onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[92vh] flex flex-col bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-gray-900 dark:text-white inline-flex items-center gap-2">
              <RotateCcw className="w-4 h-4 text-violet-500" />
              {t('videoStudio.heygenEdit.title', 'Editar e regerar apresentador')}
            </h2>
            <p className="text-[11px] text-gray-500 mt-0.5">
              {t('videoStudio.heygenEdit.subtitle', 'Ajuste o roteiro e gere uma nova versão. Avatar, voz e proporção do original são mantidos.')}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={generating}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-400 disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* HeyGen-no-edit notice */}
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-[11px] text-amber-800 dark:text-amber-200">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              {t('videoStudio.heygenEdit.noEditNotice', 'A HeyGen não permite editar um vídeo já renderizado — toda mudança gera um novo vídeo. O original continua na sua galeria.')}
            </span>
          </div>

          {/* Original metadata summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
            <Tile label={t('videoStudio.heygenEdit.avatar', 'Avatar')} value={original.avatar_id ? `#${String(original.avatar_id).slice(0, 8)}` : '—'} />
            <Tile label={t('videoStudio.heygenEdit.voice', 'Voz')} value={original.voice_id ? `#${String(original.voice_id).slice(0, 8)}` : '—'} />
            <Tile label={t('videoStudio.heygen.aspectRatio', 'Proporção')} value={video.aspect_ratio || '—'} />
            <Tile label={t('videoStudio.heygenEdit.duration', 'Duração orig.')} value={video.duration_seconds ? `${Math.round(video.duration_seconds)}s` : '—'} />
          </div>

          {/* Script editor */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Mic className="w-3.5 h-3.5" />
              {t('videoStudio.heygenEdit.scriptLabel', 'Roteiro (o que o avatar vai falar)')}
            </label>
            <textarea
              value={script}
              onChange={(e) => setScript(e.target.value)}
              rows={8}
              disabled={generating}
              className="w-full px-4 py-3 rounded-xl bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:border-primary-500/50 resize-none disabled:opacity-50"
            />
            <div className="text-[11px] text-gray-500 mt-1.5 flex items-center justify-between">
              <span>{t('videoStudio.heygen.wordsHint', { words: wordsCount, seconds: estSeconds })}</span>
              <span className="text-gray-600">{t('videoStudio.heygen.wordsTip')}</span>
            </div>
            <div className="mt-2">
              <AIPromptHelper engine="heygen" currentText={script} onApply={setScript} />
            </div>
          </div>

          {/* Caption toggle */}
          <button
            type="button"
            onClick={() => setCaption(!caption)}
            disabled={generating}
            className={`w-full inline-flex items-center justify-between gap-3 px-4 py-3 rounded-xl border transition disabled:opacity-50 ${
              caption
                ? 'bg-fuchsia-500/15 border-fuchsia-500/50 text-gray-900 dark:text-white'
                : 'bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5'
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className={`w-9 h-5 rounded-full relative transition ${caption ? 'bg-fuchsia-500' : 'bg-gray-300 dark:bg-gray-700'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${caption ? 'left-4' : 'left-0.5'}`} />
              </span>
              <span className="text-sm font-semibold">{t('videoStudio.heygen.captionToggle')}</span>
            </div>
            <span className="text-[10px] text-gray-500 text-right">
              {caption ? t('videoStudio.heygen.captionOn') : t('videoStudio.heygen.captionOff')}
            </span>
          </button>

          {/* Cost preview */}
          <div className="rounded-xl bg-gradient-to-br from-gray-900/80 to-gray-950 border border-gray-200 dark:border-gray-800 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-gray-300 text-xs uppercase tracking-wider font-semibold">
                <Coins className="w-3.5 h-3.5" />
                {t('videoStudio.heygen.estimatedCost')}
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-bold text-white">{estimate ?? '—'}</span>
                <span className="text-xs text-gray-400">{t('videoStudio.heygen.credits')}</span>
              </div>
            </div>

            {progress > 0 && generating && (
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-400">{progressLabel}</span>
                  <span className="text-xs text-gray-300 tabular-nums">{progress}%</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-gray-800 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-violet-500 to-purple-500 transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between gap-2">
          <span className="text-[11px] text-gray-500">
            {dirty
              ? t('videoStudio.heygenEdit.unsavedChanges', 'Mudanças prontas pra regerar')
              : t('videoStudio.heygenEdit.noChanges', 'Edite o roteiro pra liberar o botão de regerar')}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={generating}
              className="px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300 disabled:opacity-50"
            >
              {t('common.close', 'Fechar')}
            </button>
            <button
              onClick={handleRegenerate}
              disabled={generating || !dirty || !script.trim()}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 text-white text-sm font-semibold inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-50"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {t('videoStudio.heygen.generating')}</>
              ) : (
                <><Wand2 className="w-4 h-4" /> {t('videoStudio.heygenEdit.regenerateCta', 'Regerar vídeo')} <Sparkles className="w-3.5 h-3.5 opacity-70" /></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Tile: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-lg bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-800 px-2 py-1.5">
    <p className="text-[9px] font-semibold uppercase tracking-wider text-gray-500">{label}</p>
    <p className="text-xs font-mono tabular-nums text-gray-900 dark:text-white truncate">{value}</p>
  </div>
);

export default HeygenEditModal;
